import { useNavigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { History, Loader2, RotateCcw } from "lucide-react";
import type { DriveItem } from "@/api/driveApi";
import { useItemActivity } from "@/api/hooks";
import { Button } from "@/components/ui/button";
import { ActivityRow } from "./activityText";

/** The details pane's "Activity" tab — one item's timeline (a folder's includes its children). */
export function ItemActivity({ item }: { item: DriveItem }) {
    const auth = useAuth();
    const me = auth.user?.profile.email;
    const navigate = useNavigate();
    const activity = useItemActivity(item.id);
    const rows = activity.data?.pages.flatMap((p) => p.items) ?? [];

    if (activity.isPending) {
        return (
            <div className="space-y-4 p-4" aria-busy="true" aria-label="Loading activity">
                {Array.from({ length: 5 }, (_, i) => (
                    <div key={i} className="flex gap-3">
                        <div className="h-8 w-8 animate-pulse rounded-full bg-muted" />
                        <div className="flex-1 space-y-2 pt-1">
                            <div className="h-3 w-4/5 animate-pulse rounded bg-muted" />
                            <div className="h-2.5 w-1/3 animate-pulse rounded bg-muted" />
                        </div>
                    </div>
                ))}
            </div>
        );
    }
    if (activity.isError) {
        return (
            <div className="flex flex-col items-center gap-3 p-8 text-center text-sm text-muted-foreground">
                <p>Couldn't load activity. {activity.error.message}</p>
                <Button variant="outline" size="sm" onClick={() => void activity.refetch()}>
                    <RotateCcw className="h-4 w-4" /> Try again
                </Button>
            </div>
        );
    }
    if (rows.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center gap-3 p-8 text-center text-sm text-muted-foreground">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted">
                    <History className="h-6 w-6" aria-hidden />
                </div>
                No activity yet. Uploads, edits, moves and sharing will show up here.
            </div>
        );
    }

    return (
        <div className="p-2">
            <ol className="space-y-0.5" aria-label={`Activity for ${item.name}`}>
                {rows.map((a) => (
                    <li key={a.id}>
                        <ActivityRow
                            activity={a}
                            me={me}
                            // Rows about this item itself go nowhere new; a folder's children open.
                            onOpen={a.itemId === item.id ? undefined : (x) => navigate(`/open/${encodeURIComponent(x.itemId)}`)}
                        />
                    </li>
                ))}
            </ol>
            {activity.hasNextPage ? (
                <div className="px-2 py-3">
                    <Button
                        variant="ghost"
                        size="sm"
                        className="w-full"
                        disabled={activity.isFetchingNextPage}
                        onClick={() => void activity.fetchNextPage()}
                    >
                        {activity.isFetchingNextPage ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                        {activity.isFetchingNextPage ? "Loading…" : "Show older activity"}
                    </Button>
                </div>
            ) : null}
        </div>
    );
}
